import { action, observable, computed, makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "./rootStore";
import BundleStore from "./bundleStore";
import { CountriesDto, CountryDto, PricesList, Rates, PurchaseDto } from "../models/dtoTypes";
import { checkDiscountCode, fetchCountries, getPaymentMode, purchasePost } from "../api/orderService";

export default class PurchaseStore {
    rootStore: RootStore | undefined;
    @observable bundle: BundleStore;
    @observable countries: CountryDto[] = [];
    //which checkout to present: '' (loading), 'stripe' or 'maintenance' (decided server-side)
    @observable paymentProvider: string = '';
    //Stripe as merchant of record: hide the manual invoice form (Stripe issues the invoice)
    @observable managedPayments: boolean = false;
    //managed + recurring Price: the purchase is an auto-renewing annual subscription
    @observable subscriptionMode: boolean = false;
    //monthly recurring Price configured too: the Monthly/Yearly billing switch is offered
    @observable monthlyAvailable: boolean = false;
    //the period the user picked; may run ahead of the prices/paymentmode fetches, so it is
    //re-applied to the bundle when the monthly matrix arrives and clamped if monthly is off
    @observable desiredBillingPeriod: 'annual' | 'monthly' = 'annual';

    constructor(rootStore: RootStore, qnt: number) {
        this.rootStore = rootStore;
        this.bundle = new BundleStore(rootStore, qnt);
        this.tryRestorePurchaseState();

        getPaymentMode().then((mode) => {
            runInAction(() => {
                this.paymentProvider = mode.provider;
                this.managedPayments = mode.managedPayments;
                this.subscriptionMode = mode.subscriptionMode;
                this.monthlyAvailable = mode.monthlyAvailable ?? false;
                if (!this.monthlyAvailable) {
                    //monthly was switched off server-side - never quote (or submit) monthly prices
                    this.desiredBillingPeriod = 'annual';
                    this.bundle.setBillingPeriod('annual');
                }
            });
        }).catch(() => {
            //if we cannot determine the region, fail safe to the maintenance page rather than
            //risk routing an EU buyer to Stripe (which does not handle EU VAT)
            runInAction(() => { this.paymentProvider = 'maintenance'; });
        });

        fetchCountries().then((response) => {
            runInAction(() => {
                if (response.detectedCountryCode !== undefined && response.detectedCountryCode !== null) {
                    this.invoiceCountryIsoCode = response.detectedCountryCode;
                } else {
                    this.invoiceCountryIsoCode = 'GB';
                }
                this.countries = response.countries;
            });
        }).catch((error) => {
            console.error('Failed to fetch countries:', error);
        });
        makeAutoObservable(this);
    }

    @action setPricesAndRates(priceList: PricesList, rates: Rates, monthlyPriceList?: PricesList) {
        this.bundle.setPricesAndRates(priceList, rates, monthlyPriceList);
        //the user may have picked monthly before the matrix arrived - apply it now
        this.bundle.setBillingPeriod(this.desiredBillingPeriod);
    }

    @computed get isMonthly(): boolean { return this.bundle.IsMonthly; }

    @action setBillingPeriod(period: 'annual' | 'monthly') {
        if (period === 'monthly' && !this.monthlyAvailable && this.paymentProvider !== '') return;
        this.desiredBillingPeriod = period;
        this.bundle.setBillingPeriod(period);
        //discount codes apply to annual billing only
        if (period === 'monthly') {
            this.iHaveDiscount = false;
            this.removeDiscountCode();
        }
    }

    //#region Discount code properties
    @observable iHaveDiscount: boolean = false;
    @action setIHaveDiscount(value: boolean) {
        this.iHaveDiscount = value;
    }
    @observable discountCode: string = '';
    @observable showCheckingDiscountSpinner: boolean = false;
  
    @observable discountPercentage: number = 0;
    @computed get discountPercentageString() {
        return this.discountPercentage.toFixed(0);
    }

    @observable discountMinimumQnt: number = 1;
    @computed get discountMinimumQntString() {
        return this.discountMinimumQnt.toFixed(0);
    }

    @computed get isDiscountQntMatched() {
        if (this.discountMinimumQnt > 1 && this.bundle.Qnt < this.discountMinimumQnt) {
            return false;
        }
        return true;
    }

    
    @action setDiscountCode(value: string) {
        this.discountError = '';
        this.discountPercentage = 0;

        //trimming and uppercasing the discount code, then checking its length
        let dCode: string = value.trim().toUpperCase();
        this.discountCode = dCode;
        if (dCode.length !== 6) {
            this.showCheckingDiscountSpinner = false;
            return;
        }
        this.discountCode = dCode;
        this.showCheckingDiscountSpinner = true;

        checkDiscountCode(dCode).then((response) => {
            runInAction(() => {
                this.showCheckingDiscountSpinner = false;
                //the code was cleared or changed while this request was in flight (e.g. the user
                //switched to monthly billing, which removes the discount) - drop the late response
                if (this.discountCode !== dCode || !this.iHaveDiscount) return;
                this.isDiscountValid = response.isDiscountCodeValid;
                if (response.isDiscountCodeValid) {
                     this.discountMinimumQnt = response.minimumPurchaseQnt;
                    this.discountPercentage = response.discountPercentage;
                   
                }
                else {
                    this.discountPercentage = 0;
                }

                this.discountError = response.discountCodeError;
            });
        }).catch((error) => {
            this.showCheckingDiscountSpinner = false;
            this.discountError = error.message;
        });

    }

    @action removeDiscountCode() {
        this.discountCode = '';
        this.discountError = '';
        this.discountPercentage = 0;
        this.discountMinimumQnt = 1;
        this.isDiscountValid = false;

    }

    @action increaseQntToMatchDiscountMinimumQnt() {
        if (this.discountMinimumQnt > this.bundle.Qnt) {
            this.bundle.setQnt(this.discountMinimumQnt);
        }
    }

    @observable discountError: string = '';

    @observable isDiscountValid: boolean = false;

    @computed get codeDiscount() {
        //no discount unless the checkbox is on and billing is annual (codes are annual-only) -
        //keeps the displayed total honest even if stale percentage state lingers
        if (!this.iHaveDiscount || this.isMonthly) {
            return 0;
        }
        if(this.discountMinimumQnt > this.bundle.Qnt) {
            return 0;
        }
        return this.bundle.BundlePrice * this.discountPercentage / 100;
    }
    @computed get codeDiscountString() {
        return this.codeDiscount.toFixed(2);
    }

    @computed get totalDiscount() {
        return this.codeDiscount + this.bundle.Saving;
    }
    
    @computed get totalDiscountString() {
        return this.totalDiscount.toFixed(2);
    }
    @computed get totalAfterDiscount() {
        return this.bundle.BundlePrice - this.codeDiscount;
    }
    @computed get totalAfterDiscountString() {
        return this.totalAfterDiscount.toFixed(2);
    }

    //#endregion

    //#region Prices and rates

    @computed get USDPrice(): number { return this.totalAfterDiscount * this.bundle.rates["GBPUSD"]; }
    @computed get EURPrice(): number { return this.totalAfterDiscount * this.bundle.rates["GBPEUR"]; }

    //computer property for the price in USD and EUR based on the current exchange rates as string with 2 decimal places
    @computed get USDPriceString(): string { return this.USDPrice.toFixed(0); }
    @computed get EURPriceString(): string { return this.EURPrice.toFixed(0); }

    //#endregion

    //#region invoice properties
    @observable isInvoiceNeeded: boolean = false;
    @action setInvoiceNeeded(value: boolean) {
        this.isInvoiceNeeded = value;
    }

    @observable invoiceRefNumber: string = '';
    @action setInvoiceRefNumber(value: string) {
        this.invoiceRefNumber = value;
    }

    fieldStyleNormal: string = "";
    fieldStyleError: string = "field-err-input";

    @observable invoiceBusinessNameTouched: boolean = false;
    @observable invoiceBusinessName: string = '';
    @action setInvoiceBusinessName(value: string) {
        this.invoiceBusinessName = value;
        this.invoiceBusinessNameTouched = true;
    }
    @computed get invoiceBusinessNameFieldStyle() {
        if (this.invoiceBusinessNameTouched && this.invoiceBusinessName.trim() === '') {
            return this.fieldStyleError;
        }
        return this.fieldStyleNormal;
    }

    @observable invoiceAddress: string = '';
    @action setInvoiceAddress(value: string) {
        this.invoiceAddress = value;
        this.invoiceAddressTouched = true;
    }
    @observable invoiceAddressTouched: boolean = false;
    @computed get invoiceAddressFieldStyle() {
        if (this.invoiceAddressTouched && this.invoiceAddress.trim() === '') {
            return this.fieldStyleError;
        }
        return this.fieldStyleNormal;
    }

    @observable invoiceCountryIsoCodeTouched: boolean = false;
    @observable invoiceCountryIsoCode: string = '';
    @action setInvoiceCountryIsoCode(value: string) {
        this.invoiceCountryIsoCode = value;
        this.invoiceCountryIsoCodeTouched = true;
    }
    @computed get invoiceCountryIsoCodeFieldStyle() {
        if (this.invoiceCountryIsoCodeTouched && this.invoiceCountryIsoCode.trim() === '') {
            return this.fieldStyleError;
        }
        return this.fieldStyleNormal;
    }

    @computed get isInvoiceDataValid() {
        if (this.isInvoiceNeeded) {
            if (this.invoiceBusinessName === '') {
                return false;
            }
            if (this.invoiceAddress === '') {
                return false;
            }
            if (this.invoiceCountryIsoCode === '') {
                return false;
            }
        }
        return true;
    }
    //#endregion

    // proccesing order properties
    @observable isCheckoutClicked: boolean = false;

    @action HandleCheckoutClicked() {
        this.isCheckoutClicked = true;
        this.invoiceBusinessNameTouched = true;
        this.invoiceAddressTouched = true;
        this.invoiceCountryIsoCodeTouched = true;
        if (!this.isInvoiceDataValid) {
            this.isCheckoutClicked = false;
            return;
        }

        try {
            this.PurchaseError = '';
            var purchaseDto: PurchaseDto = {
                Url: window.location.origin,
                Qnt: this.bundle.Qnt,
                BillingPeriod: this.bundle.billingPeriod,
                //discount codes are annual-only; never send one with a monthly checkout
                DiscountCode: this.iHaveDiscount && !this.isMonthly ? this.discountCode : '',
                //only send invoice details when the customer ticked "I need an invoice" - otherwise
                //the backend auto-generates one from these fields, and stale values must never leak
                Name: this.isInvoiceNeeded ? this.invoiceBusinessName : '',
                Address: this.isInvoiceNeeded ? this.invoiceAddress : '',
                BuyersRefNumber: this.isInvoiceNeeded ? this.invoiceRefNumber : '',
                CountryIsoCode: this.invoiceCountryIsoCode,
                InvoiceNeeded: this.isInvoiceNeeded,
                BuyersVatNumber: ''
            };
            this.savePurchaseState(purchaseDto);

            //Stripe hosted checkout - redirect; Stripe returns to /payment/success/{paymentId}
            purchasePost(purchaseDto).then((redirectUrl) => {
                if (!redirectUrl) {
                    runInAction(() => {
                        this.PurchaseError = "Something went wrong, please try again later.";
                        this.isCheckoutClicked = false;
                    });
                    return;
                }
                window.location.href = redirectUrl;
            }).catch((error) => {
                runInAction(() => {
                    console.error('Failed to purchase:', error);
                    this.PurchaseError = "Something went wrong: " + error.message;
                    this.isCheckoutClicked = false;
                });
            });
        } catch (error) {
            runInAction(() => {
                console.error('Failed to create purchaseDto:', error);
                this.PurchaseError = "Something went wrong: ";
                this.isCheckoutClicked = false;
            });
            return;
        }

    }
    savePurchaseState(purchaseDto: PurchaseDto) {
        localStorage.setItem('purchaseDto', JSON.stringify(purchaseDto));
    }

    tryRestorePurchaseState() {
        var purchaseDto = localStorage.getItem('purchaseDto');
        if (purchaseDto !== null) {
            var dto = JSON.parse(purchaseDto);
            //invoice details are intentionally NOT restored: the country is forced from the
            //visitor's IP and the invoice is opt-in per purchase, so stale values never carry over
            this.iHaveDiscount = dto.DiscountCode !== '';
            this.discountCode = dto.DiscountCode;
            this.bundle.setQnt(dto.Qnt);
            //the billing period survives the login round-trip; the bundle picks it up once the
            //monthly matrix arrives (setPricesAndRates), and paymentmode clamps it if monthly is off
            if (dto.BillingPeriod === 'monthly') {
                this.desiredBillingPeriod = 'monthly';
            }
        }
    }

    //called after a successful purchase: clear the saved state (and the in-memory singleton fields)
    //so a brand-new purchase doesn't restore the now-used discount code or stale invoice details
    @action clearPurchaseState() {
        localStorage.removeItem('purchaseDto');
        this.desiredBillingPeriod = 'annual';
        this.bundle.setBillingPeriod('annual');
        this.iHaveDiscount = false;
        this.discountCode = '';
        this.discountPercentage = 0;
        this.discountMinimumQnt = 1;
        this.isDiscountValid = false;
        this.discountError = '';
        this.showCheckingDiscountSpinner = false;
        this.isInvoiceNeeded = false;
        this.invoiceBusinessName = '';
        this.invoiceAddress = '';
        this.invoiceRefNumber = '';
    }

    @observable PurchaseError: string = '';

    @computed get isOrderValid() {
        if (!this.isInvoiceDataValid) {
            return false;
        }
        return this.bundle.Qnt > 0;
    }


}